import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../Settings.css';
import { apiFetch } from '../utils/api';
import { API_BASE_URL } from '../config';

type SettingsState = {
  keg_deposit_price: string;
  invoice_next_E: string;
  invoice_next_ATH: string;
  invoice_next_MTR: string;
  invoice_next_ATR: string;
  to_next: string;
};

type StorageCategory = { id: string; label: string };
type StorageDefault = {
  siteId: string;
  siteName?: string;
  category: string;
  locationId?: number | null;
  locationName?: string | null;
};
type SiteRow = { siteId: string; name: string };
type LocationRow = { locationId: number; name: string; siteId: string };

const Settings: React.FC = () => {
  const [settings, setSettings] = useState<SettingsState>({
    keg_deposit_price: '0.00',
    invoice_next_E: '1',
    invoice_next_ATH: '1',
    invoice_next_MTR: '1',
    invoice_next_ATR: '1',
    to_next: '1',
  });
  const [categories, setCategories] = useState<StorageCategory[]>([]);
  const [defaults, setDefaults] = useState<StorageDefault[]>([]);
  const [sites, setSites] = useState<SiteRow[]>([]);
  const [locations, setLocations] = useState<LocationRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savingDefaults, setSavingDefaults] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSettings();
    void fetchStorageDefaults();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await apiFetch('/api/settings');
      if (!response.ok) throw new Error(`Failed to fetch settings: ${response.statusText}`);
      const data = await response.json();
      setSettings({
        keg_deposit_price: data.keg_deposit_price || '0.00',
        invoice_next_E: data.invoice_next_E || data.invoice_numbering?.E?.next?.toString() || '1',
        invoice_next_ATH: data.invoice_next_ATH || data.invoice_numbering?.ATH?.next?.toString() || '1',
        invoice_next_MTR: data.invoice_next_MTR || data.invoice_numbering?.MTR?.next?.toString() || '1',
        invoice_next_ATR: data.invoice_next_ATR || data.invoice_numbering?.ATR?.next?.toString() || '1',
        to_next: data.to_next || data.to_numbering?.next?.toString() || '1',
      });
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      setError(errorMessage);
    }
  };

  const fetchStorageDefaults = async () => {
    try {
      const [defRes, sitesRes, locsRes] = await Promise.all([
        apiFetch(`${API_BASE_URL}/api/storage-defaults`),
        apiFetch(`${API_BASE_URL}/api/sites`),
        apiFetch(`${API_BASE_URL}/api/locations`),
      ]);
      if (defRes.ok) {
        const data = await defRes.json();
        setCategories(data.categories || []);
        setDefaults(data.defaults || []);
      }
      if (sitesRes.ok) {
        const data = await sitesRes.json();
        setSites(Array.isArray(data) ? data : []);
      }
      if (locsRes.ok) {
        const data = await locsRes.json();
        setLocations(Array.isArray(data) ? data : []);
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load storage defaults';
      setError(errorMessage);
    }
  };

  const brewerySites = sites.filter((s) => /BR-AL-/i.test(s.siteId) || /brewery/i.test(s.name));

  const locationFor = (siteId: string, category: string) => {
    const hit = defaults.find((d) => d.siteId === siteId && d.category === category);
    return hit?.locationId != null ? String(hit.locationId) : '';
  };

  const setLocationFor = (siteId: string, category: string, locationId: string) => {
    setDefaults((prev) => {
      const next = prev.filter((d) => !(d.siteId === siteId && d.category === category));
      next.push({
        siteId,
        category,
        locationId: locationId ? Number(locationId) : null,
        locationName: locations.find((l) => String(l.locationId) === locationId)?.name || null,
        siteName: sites.find((s) => s.siteId === siteId)?.name,
      });
      return next;
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSettings((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    const kegDepositPrice = parseFloat(settings.keg_deposit_price);
    if (isNaN(kegDepositPrice) || kegDepositPrice < 0) {
      setError('Keg deposit price must be a non-negative number');
      return;
    }
    const nextE = parseInt(settings.invoice_next_E, 10);
    const nextAth = parseInt(settings.invoice_next_ATH, 10);
    const nextMtr = parseInt(settings.invoice_next_MTR, 10);
    const nextAtr = parseInt(settings.invoice_next_ATR, 10);
    const nextTo = parseInt(settings.to_next, 10);
    if (!Number.isFinite(nextE) || nextE < 1) {
      setError('Next Madison invoice number (E-) must be a positive integer');
      return;
    }
    if (!Number.isFinite(nextAth) || nextAth < 1) {
      setError('Next Athens invoice number (ATH-) must be a positive integer');
      return;
    }
    if (!Number.isFinite(nextMtr) || nextMtr < 1) {
      setError('Next Madison taproom number (MTR-) must be a positive integer');
      return;
    }
    if (!Number.isFinite(nextAtr) || nextAtr < 1) {
      setError('Next Athens taproom number (ATR-) must be a positive integer');
      return;
    }
    if (!Number.isFinite(nextTo) || nextTo < 1) {
      setError('Next Transfer Order (TO) number must be a positive integer');
      return;
    }
    setSaving(true);
    try {
      const response = await apiFetch('/api/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          keg_deposit_price: kegDepositPrice.toFixed(2),
          invoice_next_E: String(nextE),
          invoice_next_ATH: String(nextAth),
          invoice_next_MTR: String(nextMtr),
          invoice_next_ATR: String(nextAtr),
          to_next: String(nextTo),
        }),
      });
      if (!response.ok) {
        let message = `Failed to save settings: ${response.statusText}`;
        try {
          const body = await response.json();
          message = body.error || message;
        } catch {
          /* keep statusText */
        }
        throw new Error(message);
      }
      const data = await response.json();
      setSettings({
        keg_deposit_price: data.keg_deposit_price || kegDepositPrice.toFixed(2),
        invoice_next_E: data.invoice_next_E || String(nextE),
        invoice_next_ATH: data.invoice_next_ATH || String(nextAth),
        invoice_next_MTR: data.invoice_next_MTR || String(nextMtr),
        invoice_next_ATR: data.invoice_next_ATR || String(nextAtr),
        to_next: data.to_next || String(nextTo),
      });
      setSuccess('Settings saved successfully');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const saveStorageDefaults = async () => {
    setSavingDefaults(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = [];
      for (const site of brewerySites) {
        for (const cat of categories) {
          payload.push({
            siteId: site.siteId,
            category: cat.id,
            locationId: locationFor(site.siteId, cat.id) || null,
          });
        }
      }
      const res = await apiFetch(`${API_BASE_URL}/api/storage-defaults`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ defaults: payload }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setDefaults(data.defaults || []);
      setSuccess('Storage location defaults saved');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save storage defaults');
    } finally {
      setSavingDefaults(false);
    }
  };

  return (
    <div className="page-container settings-container">
      <h2>Facility Settings</h2>
      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}
      <div style={{ marginBottom: '1.25rem' }}>
        <button
          type="button"
          onClick={() => navigate('/settings/quickbooks')}
          style={{ minHeight: 44, fontSize: 16 }}
        >
          QuickBooks
        </button>
        <p style={{ fontSize: '0.9rem', opacity: 0.85, marginTop: '0.35rem' }}>
          Connect QuickBooks Online and map customers/items so approved invoices sync one-way.
        </p>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="keg_deposit_price">Keg Deposit Price ($):</label>
          <input
            type="number"
            id="keg_deposit_price"
            name="keg_deposit_price"
            value={settings.keg_deposit_price}
            onChange={handleChange}
            step="0.01"
            min="0"
            required
          />
        </div>

        <h3 style={{ marginTop: '1.5rem' }}>Invoice numbering</h3>
        <p style={{ fontSize: '0.9rem', opacity: 0.85 }}>
          Madison sites (BR-AL-20019, DSP-AL-20010, TAPROOM-MADISON) issue{' '}
          <strong>E-…</strong>. Athens sites (BR-AL-20088, DSP-AL-20051, TAPROOM-ATHENS) issue{' '}
          <strong>ATH-…</strong>. Taproom orders have their own series and never use E-/ATH-:
          Madison taproom <strong>MTR-…</strong>, Athens taproom <strong>ATR-…</strong>. The next
          number must not already exist.
        </p>
        <div className="form-group">
          <label htmlFor="invoice_next_E">Next Madison invoice number (E-…):</label>
          <input
            type="number"
            id="invoice_next_E"
            name="invoice_next_E"
            value={settings.invoice_next_E}
            onChange={handleChange}
            min="1"
            step="1"
            required
          />
          <small>Example: E-{settings.invoice_next_E || '?'}</small>
        </div>
        <div className="form-group">
          <label htmlFor="invoice_next_ATH">Next Athens invoice number (ATH-…):</label>
          <input
            type="number"
            id="invoice_next_ATH"
            name="invoice_next_ATH"
            value={settings.invoice_next_ATH}
            onChange={handleChange}
            min="1"
            step="1"
            required
          />
          <small>Example: ATH-{settings.invoice_next_ATH || '?'}</small>
        </div>
        <div className="form-group">
          <label htmlFor="invoice_next_MTR">Next Madison taproom order number (MTR-…):</label>
          <input
            type="number"
            id="invoice_next_MTR"
            name="invoice_next_MTR"
            value={settings.invoice_next_MTR}
            onChange={handleChange}
            min="1"
            step="1"
            required
          />
          <small>Example: MTR-{settings.invoice_next_MTR || '?'}</small>
        </div>
        <div className="form-group">
          <label htmlFor="invoice_next_ATR">Next Athens taproom order number (ATR-…):</label>
          <input
            type="number"
            id="invoice_next_ATR"
            name="invoice_next_ATR"
            value={settings.invoice_next_ATR}
            onChange={handleChange}
            min="1"
            step="1"
            required
          />
          <small>Example: ATR-{settings.invoice_next_ATR || '?'}</small>
        </div>

        <h3 style={{ marginTop: '1.5rem' }}>Transfer Order (TO) numbering</h3>
        <p style={{ fontSize: '0.9rem', opacity: 0.85 }}>
          Alabama ABC Transfer Order numbers are shared across sites. New transfers auto-assign the
          next value (unique). Imported Ekos transfers leave TO # blank until filled.
        </p>
        <div className="form-group">
          <label htmlFor="to_next">Next TO number:</label>
          <input
            type="number"
            id="to_next"
            name="to_next"
            value={settings.to_next}
            onChange={handleChange}
            min="1"
            step="1"
            required
          />
          <small>Example next TO #: {settings.to_next || '?'}</small>
        </div>

        <button type="submit" disabled={saving}>
          {saving ? 'Saving…' : 'Save Settings'}
        </button>
        <button type="button" onClick={() => navigate('/')} disabled={saving}>
          Back
        </button>
      </form>

      <h3 style={{ marginTop: '2rem' }}>Default storage locations</h3>
      <p style={{ fontSize: '0.9rem', opacity: 0.85 }}>
        Per-site defaults used when receiving inventory and drafting invoice PDF receipts. Items can
        override this on the item screen. Cold ingredients (hops, yeast, purees) → Cold Storage;
        grain → Grain Storage Onsite / Dry Storage; packaging → Dry Storage; finished goods → Brewery
        Cooler.
      </p>
      {brewerySites.map((site) => (
        <div key={site.siteId} style={{ marginBottom: '1.25rem' }}>
          <h4 style={{ marginBottom: '0.5rem' }}>
            {site.name} ({site.siteId})
          </h4>
          {categories.map((cat) => (
            <div className="form-group" key={`${site.siteId}-${cat.id}`}>
              <label htmlFor={`def-${site.siteId}-${cat.id}`}>{cat.label}</label>
              <select
                id={`def-${site.siteId}-${cat.id}`}
                value={locationFor(site.siteId, cat.id)}
                onChange={(e) => setLocationFor(site.siteId, cat.id, e.target.value)}
              >
                <option value="">— none —</option>
                {locations
                  .filter((l) => l.siteId === site.siteId)
                  .map((l) => (
                    <option key={l.locationId} value={String(l.locationId)}>
                      {l.name}
                    </option>
                  ))}
              </select>
            </div>
          ))}
        </div>
      ))}
      <button type="button" onClick={() => void saveStorageDefaults()} disabled={savingDefaults}>
        {savingDefaults ? 'Saving…' : 'Save storage defaults'}
      </button>
    </div>
  );
};

export default Settings;
